<template>
   <div class="insurance-container">
      <h4 v-if="insurance.length > 0">
         {{ $t("insurance_titleInsurancePackages") }}
      </h4>
      <div class="insurance-widgets">
         <!-- No Offer Selected -->
         <template v-if="!isAnySelected">
            <div v-for="(item, index) in insurance" :key="`insurance-${item.identifier}`" class="insurance-widget">
               <div class="insurance-content">
                  <!-- Top Section -->
                  <div class="insurance-top-section">
                     <div class="insurance-top-section-top">
                        <div class="insurance-header">
                           <h2 class="insurance-title">{{ item.alias }}</h2>
                           <span class="insurance-subtitle">
                              <strong>{{ handlerInsuranceSubtitle(item.comprehensive, true) }}</strong>
                              {{ handlerInsuranceSubtitle(item.comprehensive) }}
                           </span>
                        </div>
                        <span class="icon">
                           <img v-if="!item.comprehensive" :src="getShieldImage('unselected')" />
                           <img v-else :src="getShieldImage('selected')" />
                        </span>
                     </div>
                     <div class="insurance-top-section-bottom" v-if="mode === 'checkout'">
                        <div class="insurance-terms">
                           <UISwitch
                              :key="`switch-${index}`"
                              :switchInitStatus="item.isTermsAccepted"
                              class="insurance-switch"
                              :uniqueId="`switch-${index}`"
                              :disabled="item.selected"
                              @update-switch-status="updateTermsAccepted(item, $event)" />
                           <span v-html="handlerTermsAndConditionsText(item.termsAndConditionsLink)"></span>
                        </div>
                     </div>
                  </div>
                  <!-- Middle Section -->
                  <div class="insurance-middle-section">
                     <ul class="insurance-coverage">
                        <li v-for="(fee, idx) in item.coverageFees" :key="idx" class="coverage-item">
                           <i class="gts-icon icon-xs icon-tick"></i>
                           <div class="fee-details">
                              <span class="fee-name">{{ fee.name }}</span>
                              <span class="fee-price">
                                 <span class="fee-price-title"> {{ $t("insuranceModal_lblMax") }} :</span>
                                 <span style="font-weight: bold">
                                    {{ handlerInsurancePrice(fee.price) }}
                                 </span>
                              </span>
                              <span class="fee-deductible">
                                 <span class="fee-price-title"> {{ $t("insuranceModal_lblDeductible") }} :</span>
                                 <span style="font-weight: bold">
                                    {{ fee.deductible }}
                                 </span>
                              </span>
                           </div>
                        </li>
                     </ul>
                  </div>

                  <!-- Bottom Section -->
                  <div class="insurance-bottom-section">
                     <div class="insurance-price">
                        <strong class="price-amount"> + {{ item.total | price(item.currency) }} </strong>
                     </div>
                     <button v-if="mode === 'checkout'" class="btn-add" :disabled="!item.isTermsAccepted" :key="`button-${index}`" @click="toggleInsurance(item)">
                        {{ $t("insuranceWidget_btnAdd") }}
                     </button>
                  </div>
               </div>
            </div>
         </template>
         <!-- One Offer Selected -->
         <template v-else>
            <div v-for="(item, index) in selectedInsurance" :key="`insurance-selected-${item.identifier}`" class="insurance-widget selected" :class="{ 'insurance-added': mode === 'orderDetail' }">
               <div class="insurance-content selected">
                  <!-- Left Section -->
                  <div class="insurance-left-section selected">
                     <div class="insurance-left-section-top">
                        <i class="gts-icon icon-shield-tick icon-md"></i>
                     </div>
                  </div>

                  <!-- Middle Section -->
                  <div class="insurance-middle-section selected">
                     <div class="insurance-header">
                        <h2 class="insurance-title">{{ item.alias }}</h2>
                        <h1 class="insurance-subtitle">({{ item.policyDetails }})</h1>
                     </div>
                     <div class="insurance-terms">
                        <UISwitch
                           :key="`switch-selected-${index}`"
                           :switchInitStatus="item.isTermsAccepted"
                           class="insurance-switch"
                           v-if="mode === 'checkout'"
                           :uniqueId="`switch-selected-${index}`"
                           :disabled="item.selected"
                           @update-switch-status="updateTermsAccepted(item, $event)" />
                        <span v-if="mode === 'checkout'" v-html="handlerTermsAndConditionsText(item.termsAndConditionsLink)"></span>
                        <span v-else>
                           <span :href="item.termsAndConditionsLink" target="_blank" class="insurance-link">{{ $t("insuranceWidget_txtTermsAndCondition") }}</span>
                        </span>
                     </div>
                     <ul class="insurance-coverage">
                        <li v-for="(fee, idx) in item.coverageFees" :key="`selected-${idx}`" class="coverage-item">
                           <i class="gts-icon icon-xs icon-tick"></i>
                           <div class="fee-details">
                              <span class="fee-name">{{ fee.name }}</span>
                              <span class="fee-price">
                                 <span class="fee-price-title"> {{ $t("insuranceModal_lblMax") }} :</span>
                                 <span style="font-weight: bold">
                                    {{ handlerInsurancePrice(fee.price) }}
                                 </span>
                              </span>
                              <span class="fee-deductible">
                                 <span class="fee-price-title"> {{ $t("insuranceModal_lblDeductible") }} :</span>
                                 <span style="font-weight: bold">
                                    {{ fee.deductible }}
                                 </span>
                              </span>
                           </div>
                        </li>
                     </ul>
                  </div>

                  <!-- Right Section -->
                  <div class="insurance-right-section selected">
                     <div class="insurance-price">
                        <strong class="price-amount price-added">
                           {{ item.total | price(item.currency) }}
                        </strong>
                     </div>
                     <button v-if="mode === 'checkout'" class="btn-add btn-cancel" :disabled="!item.isTermsAccepted" :key="`button-selected-${index}`" @click="toggleInsurance(item)">
                        {{ $t("insuranceWidget_btnCancel") }}
                     </button>
                  </div>
               </div>
            </div>
         </template>
      </div>
   </div>
</template>

<script>
import UISwitch from "@/components/unitary/Switch.vue";

export default {
   name: "Insurance",
   components: {
      UISwitch,
   },
   props: {
      mode: {
         type: String,
         default: "checkout",
      },
      insuranceOffers: {
         type: Array,
         required: true,
      },
   },
   data() {
      const insurance = this.insuranceOffers.map((item) => ({
         ...item,
         isTermsAccepted: item.selected,
      }));

      if (insurance.length === 1) {
         insurance[0].comprehensive = true;
      } else if (insurance.length === 2) {
         const [item1, item2] = insurance;
         if (item1.total > item2.total) {
            item1.comprehensive = true;
            item2.comprehensive = false;
         } else {
            item1.comprehensive = false;
            item2.comprehensive = true;
         }
      }

      return {
         insurance,
      };
   },
   computed: {
      isAnySelected() {
         return this.insurance.some((item) => item.selected);
      },
      selectedInsurance() {
         return this.insurance.filter((item) => item.selected);
      },
   },
   mounted() {
      this.insurance.forEach((item) => {
         item.coverageFees = this.sortCoverageFees(item.coverageFees);
      });
   },
   methods: {
      toggleInsurance(item) {
         if (item.selected) {
            item.selected = false;
            item.isTermsAccepted = false;
            this.$emit("insurance-selected", "");
         } else {
            this.insurance.forEach((offer) => {
               offer.selected = false;
               offer.isTermsAccepted = false;
            });
            item.selected = true;
            item.isTermsAccepted = true;
            this.$emit("insurance-selected", item.identifier);
         }
      },
      updateTermsAccepted(item, status) {
         item.isTermsAccepted = status;
      },
      handlerTermsAndConditionsText(termsAndConditionsLink) {
         let linkHtml = `<a href="${termsAndConditionsLink}" target="_blank" style="text-decoration:underline!important;font-weight:bold;">${this.$t("insuranceWidget_txtTermsAndConditions")}</a>`;
         return this.$t("insuranceWidget_termsAndConditionsText").replace("#LINK#", linkHtml);
      },
      sortCoverageFees(coverageFees) {
         return coverageFees.filter((fee) => fee.included === undefined || fee.included);
      },
      handlerInsuranceSubtitle(comprehensive, isBold = false) {
         const boldText = comprehensive ? this.$t("insuranceWidget_txtComprehensiveInsuranceTxtStart") : this.$t("insuranceWidget_txtBasicInsuranceTxtStart");

         const regularText = comprehensive ? this.$t("insuranceWidget_txtComprehensiveInsuranceTxtEnd") : this.$t("insuranceWidget_txtBasicInsuranceTxtEnd");

         return isBold ? boldText : regularText;
      },
      handlerInsurancePrice(price) {
         switch (price) {
            case "covid_included":
               return this.$t("insuranceWidget_txtCovidIncluded");
            case "Unlimited":
               return this.$t("insuranceWidget_txtUnlimited");
            default:
               return price;
         }
      },
      getShieldImage(type) {
         const isDarkTheme = document.documentElement.classList.contains("dark-theme");
         const suffix = isDarkTheme ? "_dark" : "";
         return require(`@/assets/images/shield_${type}${suffix}.png`);
      },
   },
};
</script>

<style lang="scss">
.insurance-terms {
   a {
      color: var(--txt-color) !important;
   }
}
</style>

<style lang="scss" scoped>
.insurance-container {
   h4 {
      font-weight: 700;
      margin-bottom: 24px;
   }
   .insurance-widgets {
      display: flex;
      justify-content: space-around;

      .insurance-widget {
         border: 1px solid var(--gray-300);
         border-radius: 6px;
         width: 48%;
         padding: 1rem;
         //background-color: var(--gray-50);
         background-color: var(--gray-0);
         display: flex;
         flex-direction: column;
         gap: 1rem;
         transition: background-color 0.3s ease;
         margin-bottom: 1rem;

         &.selected {
            width: 100%;
            background-color: var(--green-100);
            border-color: var(--green-100);
         }

         &.insurance-added {
            background-color: var(--teal-0);

            .insurance-circle {
               background-color: var(--cyan-500);

               .insurance-icon {
                  // filter: brightness(100);
                  //color: var(--gray-50);
                  color: var(--gray-0);
               }
            }
         }

         .insurance-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            flex-grow: 1;

            &.selected {
               flex-direction: row;
               align-items: center;
               gap: 2rem;
            }

            .insurance-top-section {
               display: flex;
               flex-direction: column;
               gap: 1rem;

               .insurance-top-section-top {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;

                  .insurance-header {
                     display: flex;
                     margin-top: 1rem;
                     flex-direction: column;
                     gap: 0.5rem;

                     .insurance-title {
                        font-size: 1.1rem;
                        margin: 0;
                        font-weight: 700;
                     }

                     .insurance-subtitle {
                        margin: 0.3rem 0 0 0;
                        color: var(--gray-500);
                        line-height: 1.4;
                        strong {
                           font-weight: bold;
                        }
                     }
                  }

                  .icon {
                     width: 110px;
                     height: 110px;
                     min-width: 110px;
                     min-height: 110px;
                  }
               }
            }

            :root,
            .light-theme {
               .insurance-top-section-top {
                  .icon {
                     mix-blend-mode: multiply;
                  }
               }

               .insurance-top-section-bottom {
                  display: flex;
                  .insurance-terms {
                     display: flex;
                     font-size: 0.9rem;
                     align-items: center;
                     color: var(--txt-color);

                     .insurance-switch {
                        scale: 0.75;
                        transform-origin: left;
                     }

                     .insurance-link {
                        font-weight: bold;
                        color: var(--txt-color);
                        text-decoration: underline !important;
                     }
                  }
               }
            }
            .insurance-middle-section {
               &.selected {
                  width: 40%;
                  .insurance-header {
                     display: flex;
                     margin-top: 1rem;
                     align-items: center;
                     flex-direction: row;
                     gap: 0.5rem;

                     .insurance-title {
                        font-size: 1.1rem;
                        margin: 0;
                        font-weight: bold;
                     }

                     .insurance-subtitle {
                        font-size: 0.8rem;
                        color: var(--gray-500);
                        line-height: 1.4;
                        strong {
                           font-weight: bold;
                        }
                     }
                  }
                  .insurance-terms {
                     display: flex;
                     font-size: 0.9rem;
                     align-items: center;

                     .insurance-switch {
                        scale: 0.75;
                        transform-origin: left;
                     }

                     .insurance-link {
                        font-weight: bold;
                        color: var(--txt-color);
                        text-decoration: underline !important;
                     }
                  }
               }
               .insurance-coverage {
                  padding: 0;
                  margin: 0;
                  margin-top: 1rem;
                  display: flex;
                  border-radius: 8px;
                  background-color: var(--teal-0);
                  flex-direction: column;

                  .coverage-item {
                     display: flex;
                     justify-content: space-between;
                     align-items: center;
                     font-size: 0.9rem;
                     color: var(--gray-500);
                     padding: 0.5rem;

                     .coverage-name {
                        font-weight: bold;
                        flex: 2;
                     }

                     .coverage-details {
                        display: flex;
                        flex: 1;
                        justify-content: space-between;

                        .coverage-price {
                           font-weight: bold;
                        }

                        .coverage-deductible {
                           color: var(--gray-400);
                        }
                     }

                     .fee-details {
                        display: flex;
                        align-items: center;
                        width: 100%;
                        gap: 0.75rem;
                        margin-left: 0.5rem;

                        .fee-name {
                           text-wrap: nowrap;
                           color: var(--gray-800);
                        }

                        .fee-price {
                           text-align: center;
                           color: var(--gray-500);
                           text-wrap: nowrap;
                           .fee-price-title {
                              font-size: 0.8rem;
                           }
                        }

                        .fee-deductible {
                           text-align: right;
                           color: var(--gray-800);
                           text-wrap: nowrap;
                           .fee-price-title {
                              font-size: 0.8rem;
                           }
                        }
                     }
                  }
               }
            }

            .insurance-bottom-section {
               margin-top: auto;
               display: flex;
               flex-direction: column;
               align-items: center;
               gap: 0.5rem;

               .insurance-price {
                  display: flex;
                  flex-direction: column;
                  align-items: center;

                  .price-amount {
                     font-size: 1.2rem;
                     font-weight: bold;
                     transition: color 0.3s ease;

                     &.price-added {
                        color: var(--leaf-500);
                     }
                  }

                  .price-label {
                     color: var(--gray-400);
                     font-size: 0.9rem;
                  }
               }

               .btn-add {
                  width: 100%;
                  max-width: 120px;
                  background-color: var(--gray-800);
                  border: none;
                  font-weight: bold;
                  padding: 0.6rem 1.5rem;
                  border-radius: 4px;
                  //color: var(--gray-50);
                  color: var(--gray-0);
                  cursor: pointer;
                  font-size: 0.9rem;
                  transition: background-color 0.3s ease;

                  &.btn-cancel {
                     background-color: var(--red-600);
                  }

                  &:disabled {
                     background-color: var(--gray-300);
                     cursor: not-allowed;
                  }

                  &:hover {
                     transform: scale(1.05);
                  }
               }
            }

            .insurance-left-section {
               display: flex;
               height: 100%;

               .insurance-left-section-top {
                  width: 71px;
                  height: 71px;
                  margin-top: 1rem;
                  border-radius: 50%;
                  background-color: var(--cyan-500);
                  display: flex;
                  justify-content: center;
                  align-items: center;

                  .icon-shield-tick {
                     // filter: brightness(0) invert(1);
                  }
               }
            }

            .insurance-right-section {
               margin-left: auto;
               margin-right: 2rem;
               display: flex;
               flex-direction: column;
               gap: 0.25rem;
               align-items: center;

               .insurance-price {
                  display: flex;
                  flex-direction: column;
                  align-items: center;

                  .price-amount {
                     font-size: 1.8rem;
                     font-weight: bold;
                     transition: color 0.3s ease;

                     &.price-added {
                        color: var(--leaf-500);
                     }
                  }

                  .price-label {
                     color: var(--gray-400);
                     font-size: 0.9rem;
                  }
               }

               .btn-add {
                  width: 100%;
                  max-width: 120px;
                  background-color: var(--gray-800);
                  border: none;
                  padding: 0.6rem 1.5rem;
                  border-radius: 4px;
                  //color: var(--gray-50);
                  color: var(--gray-0);
                  cursor: pointer;
                  font-size: 0.9rem;
                  transition: background-color 0.3s ease;

                  &.btn-cancel {
                     background-color: var(--red-600);
                  }

                  &:disabled {
                     background-color: var(--gray-300);
                     cursor: not-allowed;
                  }
               }
            }
         }
      }
   }
}

::v-deep(.switch-label.is-active) {
   background: var(--cyan-500) !important;
   border-color: var(--cyan-500) !important;
}
</style>
